/* jshint esversion: 6, -W097 */
/* globals require, module */
'use strict';

/**
 * DisclosuresModalService
 *
 * @module enervee_utils.disclosures
 */
DisclosuresModalService.$inject = [
    '$log',
    '$q',
    '$uibModal',
    'CookiesService',
    'UtilService',
];
function DisclosuresModalService(
    $log,
    $q,
    $uibModal,
    CookiesService,
    UtilService
) {
    const _pub = {
        openFavoriteProductModal: openFavoriteProductModal,
        openSavedSearchModal: openSavedSearchModal,
        openThirdPartyModal: openThirdPartyModal,
    };
    const modalDefaults = {
        controller: disclosuresModalCtrl,
        controllerAs: 'vm',
        size: 'dialog-small',
        resolve: {
            modalContext: () => { return {}; },
        },
    };
    const complianceDisclosuresEnabled = UtilService.isFeatureEnabled('compliance_disclosures');
    const DISCLOSURES_COOKIE_NAME = 'disclosure_modals_seen';

    return _pub;

    /**
     * Opens the disclosure modal for when a user favorites a product.
     * If the privacy disclosures aren't enabled, then a promise that
     * resolves immediately is returned.
     * 
     * @returns {Promise}
     */
    function openFavoriteProductModal() {
        if (!complianceDisclosuresEnabled) return $q.resolve({ continue: true });
        const hasSeenModal = checkAndSetCookie('favorite_product');
        if (hasSeenModal) return $q.resolve({ continue: true });
        const config = {
            ariaLabelledBy: 'modalPdcFavoriteProductTitle',
            ariaDescribedBy: 'modalPdcFavoriteProductBody',
            template: require('./favoriteProductDisclosureModalView.html'),
        };
        return $uibModal.open(Object.assign(modalDefaults, config)).result.catch(modalErrorHandler);
    }

    /**
     * Opens the disclosure modal for when a user saves a search.
     * If the privacy disclosures aren't enabled, then a promise that
     * resolves immediately is returned.
     * 
     * @returns {Promise}
     */
    function openSavedSearchModal() {
        if (!complianceDisclosuresEnabled) return $q.resolve({ continue: true });
        const hasSeenModal = checkAndSetCookie('save_search');
        if (hasSeenModal) return $q.resolve({ continue: true });
        const config = {
            ariaLabelledBy: 'modalPdcSaveSearchTitle',
            ariaDescribedBy: 'modalPdcSaveSearchBody',
            template: require('./savedSearchDisclosureModalView.html'),
        };
        return $uibModal.open(Object.assign(modalDefaults, config)).result.catch(modalErrorHandler);
    }

    /**
     * Opens the general-purpose disclosure module for when the
     * user interacts with third-party interfaces.
     * If the privacy disclosures aren't enabled, then a promise that
     * resolves immediately is returned.
     * 
     * @param {string} service the snake-case name of the service the modal is loading for.
     * Used for the following CMS keys:
     * - compliance:{{ service }}
     * - base:modals:disclosures:{{ service }}_title_text
     * - base:modals:disclosures:{{ service }}_body_text
     */
    function openThirdPartyModal(service) {
        if (!complianceDisclosuresEnabled) return $q.resolve({ continue: true });
        const config = {
            ariaLabelledBy: 'modalPdcThirdPartyTitle',
            ariaDescribedBy: 'modalPdcThirdPartyBody',
            template: require('./thirdPartyDisclosureModalView.html'),
            resolve: {
                modalContext: () => { return { service: service }; },
            }
        };
        return $uibModal.open(Object.assign(modalDefaults, config)).result.catch(modalErrorHandler);
    }

    /**
     * Handles any case where the modal is dismissed. For the moment,
     * the includes clicking outside the modal or on certain links
     * in the modal. For now, we're not handling the promise that the
     * modal's `result` returns, so we'll just return a `resolve` instead
     * of a `reject`.
     * 
     * @param {*} err 
     */
    function modalErrorHandler(err) {
        if (!err || err === 'backdrop click' || err === 'cancel') return $q.resolve();
        $log.error(err);
        return $q.reject(err);
    }

    /**
     * Checks to see if the user has seen this modal yet.
     * If they haven't, the cookie is created/updated with the keys of
     * all of the disclosure modals the user has seen.
     *
     * @param {string} modalKey
     * @returns {Boolean}
     */
    function checkAndSetCookie(modalKey) {
        let disclosureModalsSeen = JSON.parse(CookiesService.get(DISCLOSURES_COOKIE_NAME) || "[]");
        let hasSeenModal = disclosureModalsSeen.includes(modalKey);
        if (!hasSeenModal) {
            disclosureModalsSeen.push(modalKey);
            CookiesService.set(DISCLOSURES_COOKIE_NAME, disclosureModalsSeen);
        }
        return hasSeenModal;
    }
}

/**
 * Creates the controller used for all disclosure modals. Very little
 * logic needs to happen on these for now, so they're pretty bare-bones.
 */
disclosuresModalCtrl.$inject = ['$uibModalInstance', 'modalContext'];
function disclosuresModalCtrl($uibModalInstance, modalContext) {
    const vm = this;
    vm.context = modalContext;

    /**
     * Closes the modal in a manner considered successful. Passes the `result` as the
     * modal promise's `resolve`d value
     * 
     * @param {*} result
     */
    vm.closeModal = (result) => {
        $uibModalInstance.close(result);
    }

    /**
     * Closes the modal in a manner considered unsuccessful. Passes the `reason` as the
     * modal promise's `reject`d value
     * 
     * @param {*} reason
     */
    vm.dismissModal = (reason) => {
        $uibModalInstance.dismiss(reason || 'cancel');
    };
}

module.exports = DisclosuresModalService;